/* ===========================================================
   Hotel Home — Hospitality Layer v3 (brand-aligned)
   Brand tokens used:
     Cormorant (serif) for display lines
     Inter (sans) for body
     Palette: black #000, cream #F5F0E6, charcoal #1a1a1a, white
   Features:
     1. Elevator door reveal on the hero (one-shot per session)
     2. Brand-matched elevator floor panel (charcoal plate, cream pressed buttons)
     3. Concierge bell + "Added to your folio" toast on add-to-cart
     4. Folio cart drawer header + button relabels (Settle folio, Return to lobby)
     5. Compliments-of-the-house free-shipping progress bar in cart drawer
     6. "May we recommend" single concierge-style upsell on product pages
     7. "Do not disturb" flippable newsletter door hanger (password page)
     8. Wrong-suite 404 reskin
     9. Folio-receipt skin on order confirmation / account orders
    10. "Now arriving" rotating announcement marquee
    11. Concierge call button (bottom-left, opens "May we assist?")
    12. First-visit welcome key tag overlay
    13. Hotel-coded copy relabels (wishlist, sold out)
   Free shipping threshold: edit HH_SHIPPING_THRESHOLD below.
   Safe to delete to revert. No external dependencies.
   =========================================================== */
(function(){
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HH_SHIPPING_THRESHOLD = 20000; /* $200.00 in cents */

  /* ---------- styles ---------- */
  var css = ''
    + ':root{'
    +   '--hh-cream:#F5F0E6;'
    +   '--hh-charcoal:#1a1a1a;'
    +   '--hh-black:#000;'
    +   '--hh-serif:Cormorant,Georgia,"Times New Roman",serif;'
    +   '--hh-sans:Inter,system-ui,-apple-system,sans-serif;'
    + '}'
    /* Hotel lobby double-door reveal */
    + '.hh-doors{position:absolute;inset:0;z-index:50;pointer-events:none;overflow:hidden}'
    + '.hh-doors__panel{position:absolute;top:0;bottom:0;width:50%;background:linear-gradient(90deg,#050505 0%,#1c1c1c 50%,#050505 100%);will-change:transform;transition:transform 1.5s cubic-bezier(.7,0,.2,1);display:flex;align-items:center;justify-content:center;box-sizing:border-box}'
    + '.hh-doors__panel--left{left:0;border-right:1px solid rgba(200,160,96,.45);box-shadow:inset -12px 0 38px rgba(0,0,0,.6)}'
    + '.hh-doors__panel--right{right:0;border-left:1px solid rgba(200,160,96,.45);box-shadow:inset 12px 0 38px rgba(0,0,0,.6)}'
    + '.hh-doors.is-open .hh-doors__panel--left{transform:translateX(-100%)}'
    + '.hh-doors.is-open .hh-doors__panel--right{transform:translateX(100%)}'
    /* Brass vertical door handles on the inner edges */
    + '.hh-doors__handle{position:absolute;top:50%;transform:translateY(-50%);width:5px;height:28%;background:linear-gradient(180deg,#e4c389 0%,#a07a3e 50%,#e4c389 100%);border-radius:3px;box-shadow:0 0 10px rgba(212,176,122,.5),inset 0 0 2px rgba(0,0,0,.35)}'
    + '.hh-doors__panel--left .hh-doors__handle{right:4%}'
    + '.hh-doors__panel--right .hh-doors__handle{left:4%}'
    /* HH favicon as the logo on each door */
    + '.hh-doors__logo{width:80px;height:80px;object-fit:contain;filter:brightness(1.25) drop-shadow(0 2px 12px rgba(0,0,0,.6));opacity:.96}'
    + '.hh-doors__mark{font-family:var(--hh-serif);font-style:italic;color:var(--hh-cream);font-size:78px;line-height:1;letter-spacing:-.02em;text-shadow:0 2px 10px rgba(0,0,0,.5);opacity:.92}'
    /* Welcome text below the doorway, fades in/out */
    + '.hh-doors__welcome{position:absolute;bottom:14%;left:50%;transform:translateX(-50%);color:var(--hh-cream);font-family:var(--hh-serif);font-style:italic;font-size:22px;letter-spacing:.35em;opacity:0;animation:hh-doors-welcome 2s ease-out forwards;z-index:1;text-transform:uppercase;white-space:nowrap}'
    + '.hh-doors__welcome::before,.hh-doors__welcome::after{content:"";display:inline-block;width:22px;height:1px;background:currentColor;vertical-align:middle;margin:0 14px;opacity:.6}'
    + '@keyframes hh-doors-welcome{0%,6%{opacity:0;transform:translateX(-50%) translateY(10px)}22%,58%{opacity:1;transform:translateX(-50%) translateY(0)}88%,100%{opacity:0;transform:translateX(-50%) translateY(0)}}'
    /* Elevator panel — brand palette */
    + '.hh-elevator{position:fixed;right:18px;bottom:24px;width:66px;background:linear-gradient(180deg,#1f1f1f 0%,#0e0e0e 100%);border:1px solid rgba(245,240,230,.18);border-radius:6px;padding:10px 9px 11px;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.25),inset 0 1px 0 rgba(245,240,230,.06);user-select:none;font-family:var(--hh-sans);transition:opacity .3s ease}'
    + '.hh-elevator__brand{text-align:center;font-family:var(--hh-serif);font-style:italic;font-size:11px;letter-spacing:.05em;color:var(--hh-cream);opacity:.7;margin-bottom:6px;line-height:1}'
    + '.hh-elevator__display{text-align:center;font-family:var(--hh-serif);font-size:15px;color:var(--hh-cream);letter-spacing:.18em;padding:4px 0 6px;margin-bottom:9px;border-bottom:1px solid rgba(245,240,230,.14);min-height:15px;font-style:italic}'
    + '.hh-elevator__floors{display:grid;grid-template-columns:1fr 1fr;gap:7px}'
    + '.hh-elevator__floor{width:22px;height:22px;border-radius:50%;background:radial-gradient(circle at 30% 28%,#3a3a3a 0%,#1c1c1c 70%);border:1px solid rgba(245,240,230,.22);color:var(--hh-cream);font-family:var(--hh-sans);font-size:10px;font-weight:500;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;transition:all .25s ease;box-shadow:inset 0 1px 0 rgba(255,255,255,.06),0 1px 0 rgba(0,0,0,.4)}'
    + '.hh-elevator__floor:hover{border-color:rgba(245,240,230,.55);color:#fff;transform:translateY(-1px)}'
    + '.hh-elevator__floor.is-active{background:var(--hh-cream);color:var(--hh-black);border-color:var(--hh-cream);box-shadow:0 0 0 2px rgba(245,240,230,.22),inset 0 1px 2px rgba(0,0,0,.25)}'
    + '@media(max-width:749px){.hh-elevator{right:10px;bottom:14px;width:52px;padding:8px 6px}.hh-elevator__floor{width:18px;height:18px;font-size:9px}.hh-elevator__display{font-size:12px}.hh-elevator__brand{font-size:10px}}'
    /* Concierge bell + toast (brand palette) */
    + '.hh-bell{position:fixed;top:80px;right:24px;width:46px;height:46px;transform-origin:50% 6px;pointer-events:none;opacity:0;z-index:10001;transition:opacity .3s}'
    + '.hh-bell.is-ringing{opacity:1;animation:hh-bell-ring 1.2s cubic-bezier(.36,.07,.19,.97)}'
    + '@keyframes hh-bell-ring{0%,100%{transform:rotate(0)}10%,30%,50%,70%{transform:rotate(-12deg)}20%,40%,60%,80%{transform:rotate(12deg)}90%{transform:rotate(0)}}'
    + '.hh-bell svg{width:100%;height:100%}'
    + '.hh-toast{position:fixed;top:96px;right:24px;background:var(--hh-black);color:var(--hh-cream);border:none;border-radius:2px;padding:11px 17px;font-family:var(--hh-sans);font-size:11px;font-weight:500;letter-spacing:.2em;text-transform:uppercase;z-index:10002;opacity:0;transform:translateY(-8px);transition:opacity .35s,transform .35s;box-shadow:0 8px 22px rgba(0,0,0,.22)}'
    + '.hh-toast.is-on{opacity:1;transform:translateY(0)}'
    + '.hh-toast::before{content:"\\2022";color:var(--hh-cream);opacity:.5;margin-right:10px;font-size:14px}'
    /* Folio cart drawer header */
    + '.hh-folio-header{text-align:center;padding:14px 18px 12px;border-bottom:1px dashed currentColor;margin:0 0 4px;font-family:var(--hh-sans);opacity:.92}'
    + '.hh-folio-header__brand{font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.04em;opacity:.7}'
    + '.hh-folio-header__title{font-family:var(--hh-serif);font-size:19px;letter-spacing:.16em;margin:4px 0 0;font-weight:400;text-transform:uppercase;font-style:italic}'
    + '.hh-folio-header__meta{font-family:var(--hh-sans);font-size:10px;letter-spacing:.22em;margin-top:6px;opacity:.55;text-transform:uppercase}'
    /* DND newsletter — brand palette */
    + '.hh-dnd{position:relative;max-width:300px;margin:32px auto;perspective:1200px;aspect-ratio:1/1.38}'
    + '.hh-dnd__card{position:relative;width:100%;height:100%;transition:transform .85s cubic-bezier(.4,.2,.2,1);transform-style:preserve-3d;cursor:pointer}'
    + '.hh-dnd.is-flipped .hh-dnd__card{transform:rotateY(180deg)}'
    + '.hh-dnd__face{position:absolute;inset:0;backface-visibility:hidden;border-radius:3px;padding:22px 18px 18px;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;box-shadow:0 22px 36px rgba(0,0,0,.24)}'
    + '.hh-dnd__face--front{background:var(--hh-black);color:var(--hh-cream);border:1px solid rgba(245,240,230,.22)}'
    + '.hh-dnd__face--back{background:var(--hh-cream);color:var(--hh-black);border:1px solid var(--hh-black);transform:rotateY(180deg)}'
    + '.hh-dnd__hole{width:14px;height:14px;border-radius:50%;background:inherit;border:1px solid currentColor;margin-bottom:14px;box-shadow:inset 0 2px 4px rgba(0,0,0,.4)}'
    + '.hh-dnd__frame{flex:1;width:100%;border:1px solid currentColor;border-radius:2px;padding:20px 14px;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:12px;opacity:.95}'
    + '.hh-dnd__title{font-family:var(--hh-serif);font-size:26px;letter-spacing:.08em;font-weight:400;text-transform:uppercase;line-height:1.1;font-style:italic}'
    + '.hh-dnd__rule{width:54%;height:1px;background:currentColor;opacity:.5}'
    + '.hh-dnd__sub{font-family:var(--hh-sans);font-size:9px;letter-spacing:.24em;opacity:.7;text-transform:uppercase}'
    + '.hh-dnd__form{display:flex;flex-direction:column;align-items:center;gap:12px;width:100%}'
    + '.hh-dnd__form input[type=email]{background:transparent;border:none;border-bottom:1px solid currentColor;padding:7px 4px;color:inherit;font-family:var(--hh-sans);font-size:13px;width:100%;text-align:center;letter-spacing:.04em;outline:none}'
    + '.hh-dnd__form input[type=email]::placeholder{color:currentColor;opacity:.45}'
    + '.hh-dnd__form button{background:var(--hh-black);color:var(--hh-cream);border:none;padding:10px 22px;font-family:var(--hh-sans);font-size:9px;font-weight:500;letter-spacing:.24em;text-transform:uppercase;cursor:pointer;border-radius:1px;margin-top:4px}'
    + '.hh-dnd__form button:hover{opacity:.9}'
    /* Compliments of the house — free shipping bar */
    + '.hh-shipping{background:var(--hh-black);color:var(--hh-cream);padding:14px 18px 16px;text-align:center;font-family:var(--hh-sans);margin:0}'
    + '.hh-shipping__label{font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.04em;line-height:1.2}'
    + '.hh-shipping__sub{font-size:9px;letter-spacing:.22em;text-transform:uppercase;margin-top:5px;opacity:.7;line-height:1.4}'
    + '.hh-shipping__bar{position:relative;height:3px;background:rgba(245,240,230,.18);margin:10px 8px 0;overflow:hidden}'
    + '.hh-shipping__fill{position:absolute;top:0;left:0;height:100%;width:0;background:var(--hh-cream);transition:width .65s cubic-bezier(.5,0,.2,1)}'
    + '.hh-shipping.is-complete .hh-shipping__bar{opacity:0;height:0;margin-top:0;transition:opacity .3s,height .3s,margin .3s}'
    + '.hh-shipping.is-complete .hh-shipping__sub{opacity:.85}'
    + '.hh-shipping__sub::before{content:""}'
    + '.hh-shipping.is-complete .hh-shipping__sub::before{content:"\\2726";opacity:.7;margin-right:8px}'
    /* May we recommend upsell */
    + '.hh-recommend{background:var(--hh-cream);color:var(--hh-black);padding:14px 18px 16px;margin:0 0 4px;border-bottom:1px solid rgba(0,0,0,.08);font-family:var(--hh-sans)}'
    + '.hh-recommend__label{font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.04em;margin-bottom:4px}'
    + '.hh-recommend__rule{width:90px;height:1px;background:currentColor;opacity:.4;margin-bottom:10px}'
    + '.hh-recommend__item{display:flex;align-items:center;gap:12px}'
    + '.hh-recommend__img{width:46px;height:46px;border-radius:2px;background:#e5dfd2 center/cover no-repeat;flex:0 0 46px}'
    + '.hh-recommend__meta{flex:1;min-width:0}'
    + '.hh-recommend__title{font-size:12px;font-weight:500;line-height:1.3;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'
    + '.hh-recommend__price{font-size:11px;opacity:.65;margin-top:2px;letter-spacing:.02em}'
    + '.hh-recommend__add{background:var(--hh-black);color:var(--hh-cream);border:none;padding:8px 14px;font-family:var(--hh-sans);font-size:9px;font-weight:500;letter-spacing:.22em;text-transform:uppercase;cursor:pointer;border-radius:1px;white-space:nowrap}'
    + '.hh-recommend__add:hover{opacity:.88}'
    + '.hh-recommend__add[disabled]{opacity:.4;cursor:default}'
    /* PDP variant of recommend */
    + '.hh-recommend--pdp{max-width:680px;margin:64px auto 32px;padding:32px 24px 28px;background:transparent;border:1px solid var(--hh-black);border-radius:0}'
    + '.hh-recommend--pdp .hh-recommend__label{font-size:18px;text-align:center;margin-bottom:6px}'
    + '.hh-recommend--pdp .hh-recommend__rule{width:60px;margin:0 auto 18px}'
    + '.hh-recommend--pdp .hh-recommend__item{gap:18px;align-items:center}'
    + '.hh-recommend--pdp .hh-recommend__img{width:88px;height:88px;flex:0 0 88px;border-radius:0}'
    + '.hh-recommend--pdp .hh-recommend__title{font-family:var(--hh-serif);font-style:italic;font-size:18px;letter-spacing:.04em;font-weight:400;line-height:1.25;white-space:normal;overflow:visible}'
    + '.hh-recommend--pdp .hh-recommend__price{font-size:13px;margin-top:4px}'
    + '.hh-recommend--pdp .hh-recommend__add{padding:12px 24px;font-size:10px;letter-spacing:.24em}'
    + '@media(max-width:599px){.hh-recommend--pdp{margin:40px 18px 28px;padding:24px 18px 22px}.hh-recommend--pdp .hh-recommend__item{flex-direction:column;text-align:center;gap:14px}.hh-recommend--pdp .hh-recommend__img{width:120px;height:120px}}'
    /* Wrong-suite 404 */
    + '.hh-404{position:fixed;inset:0;background:var(--hh-cream);color:var(--hh-black);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:9998;font-family:var(--hh-sans);padding:40px 20px;text-align:center}'
    + '.hh-404__plaque{max-width:520px;width:100%;border:1px solid var(--hh-black);padding:42px 32px 36px;background:transparent}'
    + '.hh-404__brand{font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.32em;text-transform:uppercase;opacity:.6;margin-bottom:18px}'
    + '.hh-404__title{font-family:var(--hh-serif);font-style:italic;font-size:48px;letter-spacing:.04em;line-height:1.1;margin:0}'
    + '.hh-404__rule{width:80px;height:1px;background:currentColor;opacity:.5;margin:20px auto}'
    + '.hh-404__sub{font-size:11px;letter-spacing:.22em;text-transform:uppercase;opacity:.65;margin:0 0 26px;line-height:1.5}'
    + '.hh-404__btn{background:var(--hh-black);color:var(--hh-cream);border:none;padding:12px 28px;font-family:var(--hh-sans);font-size:10px;font-weight:500;letter-spacing:.24em;text-transform:uppercase;cursor:pointer;border-radius:1px;text-decoration:none;display:inline-block}'
    + '.hh-404__btn:hover{opacity:.9}'
    /* Folio receipt skin on order pages */
    + '.hh-receipt-mark{max-width:680px;margin:24px auto 0;text-align:center;font-family:var(--hh-sans);color:var(--hh-black);padding:0 20px;position:relative}'
    + '.hh-receipt-mark__brand{font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.32em;text-transform:uppercase;opacity:.55}'
    + '.hh-receipt-mark__title{font-family:var(--hh-serif);font-style:italic;font-size:30px;letter-spacing:.06em;line-height:1.2;margin:8px 0 6px}'
    + '.hh-receipt-mark__sub{font-size:10px;letter-spacing:.22em;text-transform:uppercase;opacity:.6}'
    + '.hh-receipt-mark__rule{width:120px;height:1px;background:currentColor;opacity:.4;margin:14px auto 0}'
    + '.hh-receipt-stamp{display:inline-block;border:1.5px solid var(--hh-black);color:var(--hh-black);padding:6px 16px;font-family:var(--hh-serif);font-style:italic;font-size:13px;letter-spacing:.16em;text-transform:uppercase;transform:rotate(-3deg);margin-top:14px;opacity:.7;background:transparent}'
    + 'body.hh-receipt::before{content:"";position:fixed;inset:0;pointer-events:none;background:repeating-linear-gradient(-30deg,transparent 0 80px,rgba(0,0,0,.025) 80px 81px);z-index:-1}'
    /* Now-arriving marquee */
    + '.hh-marquee-text{display:inline-block;animation:hh-fade-line 7s ease-in-out infinite;font-family:inherit}'
    + '@keyframes hh-fade-line{0%,8%{opacity:0;transform:translateY(4px)}14%,86%{opacity:1;transform:translateY(0)}92%,100%{opacity:0;transform:translateY(-4px)}}'
    /* Concierge call button */
    + '.hh-concierge{position:fixed;left:18px;bottom:24px;width:44px;height:44px;border-radius:50%;background:var(--hh-black);border:1px solid rgba(245,240,230,.22);cursor:pointer;z-index:9999;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 18px rgba(0,0,0,.22);padding:0;transition:transform .2s ease}'
    + '.hh-concierge:hover{transform:translateY(-2px)}'
    + '.hh-concierge svg{width:22px;height:22px}'
    + '.hh-concierge__card{position:fixed;left:18px;bottom:78px;width:236px;background:var(--hh-cream);color:var(--hh-black);border:1px solid var(--hh-black);border-radius:2px;padding:16px 16px 14px;z-index:10000;font-family:var(--hh-sans);opacity:0;transform:translateY(8px);pointer-events:none;transition:opacity .25s ease,transform .25s ease;box-shadow:0 10px 28px rgba(0,0,0,.22)}'
    + '.hh-concierge__card.is-open{opacity:1;transform:translateY(0);pointer-events:auto}'
    + '.hh-concierge__title{font-family:var(--hh-serif);font-style:italic;font-size:16px;letter-spacing:.04em;line-height:1.2}'
    + '.hh-concierge__rule{width:60px;height:1px;background:currentColor;opacity:.4;margin:6px 0 10px}'
    + '.hh-concierge__links{display:flex;flex-direction:column;gap:2px}'
    + '.hh-concierge__links a{display:block;padding:6px 0;color:inherit;text-decoration:none;font-size:11px;letter-spacing:.18em;text-transform:uppercase;border-bottom:0.5px solid rgba(0,0,0,.08)}'
    + '.hh-concierge__links a:last-child{border-bottom:none}'
    + '.hh-concierge__links a:hover{opacity:.6}'
    + '@media(max-width:749px){.hh-concierge{left:10px;bottom:14px;width:38px;height:38px}.hh-concierge__card{left:10px;bottom:62px;width:214px}}'
    /* Welcome key tag overlay */
    + '.hh-welcome{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:10010;display:flex;align-items:center;justify-content:center;font-family:var(--hh-sans);opacity:0;pointer-events:none;transition:opacity .35s ease;padding:20px}'
    + '.hh-welcome.is-on{opacity:1;pointer-events:auto}'
    + '.hh-welcome__tag{width:240px;background:var(--hh-black);color:var(--hh-cream);border:1px solid rgba(245,240,230,.28);border-radius:3px;padding:42px 26px 24px;text-align:center;position:relative;transform:translateY(-12px) rotate(-1.5deg);transition:transform .55s cubic-bezier(.34,1.56,.64,1)}'
    + '.hh-welcome.is-on .hh-welcome__tag{transform:translateY(0) rotate(-1.5deg)}'
    + '.hh-welcome__tag::before{content:"";position:absolute;top:14px;left:50%;transform:translateX(-50%);width:14px;height:14px;border-radius:50%;border:1px solid var(--hh-cream);opacity:.55}'
    + '.hh-welcome__brand{font-family:var(--hh-serif);font-style:italic;font-size:11px;letter-spacing:.32em;text-transform:uppercase;opacity:.7}'
    + '.hh-welcome__suite{font-family:var(--hh-serif);font-style:italic;font-size:32px;letter-spacing:.16em;margin:12px 0 4px;line-height:1.1}'
    + '.hh-welcome__sub{font-size:9px;letter-spacing:.32em;text-transform:uppercase;opacity:.6;margin-bottom:14px}'
    + '.hh-welcome__close{background:transparent;color:var(--hh-cream);border:1px solid rgba(245,240,230,.4);font-family:var(--hh-sans);font-size:9px;letter-spacing:.24em;text-transform:uppercase;padding:7px 18px;cursor:pointer;border-radius:1px}'
    + '.hh-welcome__close:hover{background:rgba(245,240,230,.08)}'
    + '@media(prefers-reduced-motion:reduce){.hh-bell,.hh-bell.is-ringing,.hh-dnd__card,.hh-doors__panel,.hh-doors__display,.hh-shipping__fill,.hh-marquee-text,.hh-welcome,.hh-welcome__tag{animation:none!important;transition:none!important}.hh-doors{display:none}}';

  function injectStyles(){
    if(document.getElementById('hh-hospitality-styles')) return;
    var s = document.createElement('style');
    s.id = 'hh-hospitality-styles';
    s.textContent = css;
    document.head.appendChild(s);
  }

  /* ---------- Hotel lobby door reveal on the hero ---------- */
  function buildDoors(){
    if(prefersReducedMotion) return;
    if(sessionStorage.getItem('hh-doors-played-v2')) return;
    var path = location.pathname;
    if(path !== '/' && path !== '') return;
    var banner = document.querySelector('.hero-wrapper, .shopify-section.hero-wrapper, section[class*="hero" i], section[class*="banner" i], .featured-banner');
    if(!banner) return;
    var cs = getComputedStyle(banner);
    if(cs.position === 'static') banner.style.position = 'relative';
    /* Pick up the site favicon so the doors are stamped with the HH mark */
    var faviconLink = document.querySelector('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]');
    var faviconHref = faviconLink && faviconLink.href ? faviconLink.href : '';
    var doorMark = faviconHref
      ? '<img class="hh-doors__logo" src="' + faviconHref + '" alt="" aria-hidden="true">'
      : '<span class="hh-doors__mark">H</span>';
    var doors = document.createElement('div');
    doors.className = 'hh-doors';
    doors.setAttribute('aria-hidden','true');
    doors.innerHTML = ''
      + '<div class="hh-doors__panel hh-doors__panel--left">'
      +   doorMark
      +   '<span class="hh-doors__handle"></span>'
      + '</div>'
      + '<div class="hh-doors__panel hh-doors__panel--right">'
      +   doorMark
      +   '<span class="hh-doors__handle"></span>'
      + '</div>'
      + '<div class="hh-doors__welcome">Welcome</div>';
    banner.appendChild(doors);
    sessionStorage.setItem('hh-doors-played-v2','1');
    requestAnimationFrame(function(){
      setTimeout(function(){ doors.classList.add('is-open'); }, 700);
      setTimeout(function(){ if(doors.parentNode) doors.parentNode.removeChild(doors); }, 2500);
    });
  }

  /* ---------- Elevator panel ---------- */
  function buildElevator(){
    if(document.querySelector('.hh-elevator')) return;
    var labels = ['G','1','2','3','4','PH'];
    var wrap = document.createElement('aside');
    wrap.className = 'hh-elevator';
    wrap.setAttribute('aria-label','Floor indicator');
    var html = ''
      + '<div class="hh-elevator__brand">Hotel Home</div>'
      + '<div class="hh-elevator__display" data-display>G</div>'
      + '<div class="hh-elevator__floors" role="list">';
    for(var i=0;i<labels.length;i++){
      html += '<button class="hh-elevator__floor" type="button" data-floor="'+i+'" role="listitem" aria-label="Floor '+labels[i]+'">'+labels[i]+'</button>';
    }
    html += '</div>';
    wrap.innerHTML = html;
    document.body.appendChild(wrap);

    var display = wrap.querySelector('[data-display]');
    var floors = Array.prototype.slice.call(wrap.querySelectorAll('.hh-elevator__floor'));

    function setActive(idx){
      var safe = Math.max(0, Math.min(labels.length-1, idx));
      for(var j=0;j<floors.length;j++) floors[j].classList.toggle('is-active', j === safe);
      display.textContent = labels[safe];
    }
    function onScroll(){
      var sc = window.scrollY;
      var doc = document.documentElement;
      var max = Math.max(doc.scrollHeight - window.innerHeight, 1);
      var ratio = Math.min(1, sc / max);
      var idx = Math.round(ratio * (labels.length-1));
      setActive(idx);
    }
    onScroll();
    var ticking = false;
    window.addEventListener('scroll', function(){
      if(ticking) return;
      ticking = true;
      requestAnimationFrame(function(){ onScroll(); ticking = false; });
    }, {passive:true});

    floors.forEach(function(f, i){
      f.addEventListener('click', function(){
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        var target = (i / (labels.length - 1)) * max;
        window.scrollTo({top: target, behavior: prefersReducedMotion ? 'auto' : 'smooth'});
      });
    });
  }

  /* ---------- Concierge bell ---------- */
  var bellEl, toastEl;
  function buildBell(){
    if(document.querySelector('.hh-bell')) return;
    bellEl = document.createElement('div');
    bellEl.className = 'hh-bell';
    bellEl.setAttribute('aria-hidden','true');
    /* cream bell on a charcoal base — matches brand button (black) + button-text (cream) */
    bellEl.innerHTML = '<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><ellipse cx="32" cy="56" rx="22" ry="2.5" fill="rgba(0,0,0,0.22)"/><path d="M10 48 L54 48 L50 42 L14 42 Z" fill="#1a1a1a" stroke="#000" stroke-width="0.5"/><path d="M16 42 C16 22, 48 22, 48 42 Z" fill="#F5F0E6" stroke="#000" stroke-width="0.6"/><circle cx="32" cy="18" r="3" fill="#1a1a1a" stroke="#000" stroke-width="0.4"/><line x1="32" y1="10" x2="32" y2="16" stroke="#000" stroke-width="0.6"/></svg>';
    document.body.appendChild(bellEl);

    toastEl = document.createElement('div');
    toastEl.className = 'hh-toast';
    toastEl.textContent = 'Added to your folio';
    document.body.appendChild(toastEl);
  }

  function ringBell(){
    if(!bellEl) return;
    bellEl.classList.remove('is-ringing');
    void bellEl.offsetWidth;
    bellEl.classList.add('is-ringing');
    setTimeout(function(){ bellEl.classList.remove('is-ringing'); }, 1300);
    if(toastEl){
      toastEl.classList.add('is-on');
      clearTimeout(toastEl._t);
      toastEl._t = setTimeout(function(){ toastEl.classList.remove('is-on'); }, 2200);
    }
  }

  function watchAddToCart(){
    function watchBubble(bubble){
      if(!bubble || bubble.__hhWatched) return;
      bubble.__hhWatched = true;
      var lastCount = parseInt((bubble.textContent || '0').replace(/\D/g,''), 10) || 0;
      var lastHidden = bubble.classList.contains('hidden') || bubble.classList.contains('visually-hidden');
      function check(){
        var c = parseInt((bubble.textContent || '0').replace(/\D/g,''), 10) || 0;
        var h = bubble.classList.contains('hidden') || bubble.classList.contains('visually-hidden');
        if(c > lastCount || (lastHidden && !h && c > 0)) ringBell();
        lastCount = c;
        lastHidden = h;
      }
      new MutationObserver(check).observe(bubble, {childList:true, characterData:true, subtree:true, attributes:true, attributeFilter:['class']});
    }
    var initial = document.querySelector('.cart-bubble__text-count, .cart-bubble, [data-cart-count], cart-icon .count');
    if(initial) watchBubble(initial);
    new MutationObserver(function(){
      var b = document.querySelector('.cart-bubble__text-count, .cart-bubble, [data-cart-count], cart-icon .count');
      if(b) watchBubble(b);
    }).observe(document.body, {childList:true, subtree:true});
    var events = ['cart:update','cart-update','cart:item-added','cart:items-added','theme:cart:added','on:cart:added'];
    events.forEach(function(ev){
      document.addEventListener(ev, ringBell);
      window.addEventListener(ev, ringBell);
    });
    document.addEventListener('submit', function(e){
      var f = e.target;
      if(f && f.action && /\/cart\/add/.test(f.action)) setTimeout(ringBell, 50);
    }, true);
    document.addEventListener('click', function(e){
      var t = e.target && e.target.closest ? e.target.closest('add-to-cart-component button, [data-add-to-cart], button[name="add"]') : null;
      if(t) setTimeout(ringBell, 300);
    }, true);
  }

  /* ---------- Folio cart drawer ---------- */
  function suiteNumber(){
    var n = sessionStorage.getItem('hh-suite');
    if(!n){
      n = String(200 + Math.floor(Math.random() * 99));
      sessionStorage.setItem('hh-suite', n);
    }
    return n;
  }
  function folioNumber(){
    var n = sessionStorage.getItem('hh-folio');
    if(!n){
      n = 'HH-' + String(1000 + Math.floor(Math.random() * 9000));
      sessionStorage.setItem('hh-folio', n);
    }
    return n;
  }

  function enhanceFolio(root){
    if(!root || root.dataset.hhFolio) return;
    root.dataset.hhFolio = '1';
    var shippingBar = buildShippingBar();
    var header = document.createElement('div');
    header.className = 'hh-folio-header';
    header.innerHTML = ''
      + '<div class="hh-folio-header__brand">Hotel Home</div>'
      + '<div class="hh-folio-header__title">Your folio</div>'
      + '<div class="hh-folio-header__meta">Suite ' + suiteNumber() + ' &middot; Folio ' + folioNumber() + '</div>';
    var body = root.querySelector('.drawer__body, [part="body"], .cart-drawer__body, cart-items, .cart-items-component');
    if(body && body.parentNode){
      body.parentNode.insertBefore(shippingBar, body);
      body.parentNode.insertBefore(header, body);
    } else {
      root.prepend(header);
      root.prepend(shippingBar);
    }
    relabelFolio(root);
    refreshShipping();
  }

  /* ---------- Compliments-of-the-house free shipping bar ---------- */
  function buildShippingBar(){
    var el = document.createElement('div');
    el.className = 'hh-shipping';
    el.setAttribute('data-hh-shipping','');
    el.innerHTML = ''
      + '<div class="hh-shipping__label" data-label>Compliments of the house</div>'
      + '<div class="hh-shipping__sub" data-sub>Loading...</div>'
      + '<div class="hh-shipping__bar"><div class="hh-shipping__fill" data-fill></div></div>';
    return el;
  }

  function formatMoney(cents, currency){
    var n = (cents / 100);
    try {
      return new Intl.NumberFormat(undefined, {style:'currency', currency: currency || 'USD', minimumFractionDigits: n % 1 === 0 ? 0 : 2}).format(n);
    } catch(e){ return '$' + n.toFixed(0); }
  }

  function refreshShipping(){
    fetch('/cart.json', {credentials:'same-origin', headers:{'Accept':'application/json'}})
      .then(function(r){ return r.json(); })
      .then(function(cart){
        var bars = document.querySelectorAll('.hh-shipping');
        if(!bars.length) return;
        var total = cart.total_price || 0;
        var threshold = HH_SHIPPING_THRESHOLD;
        var remaining = Math.max(0, threshold - total);
        var pct = Math.min(100, Math.round((total / threshold) * 100));
        var currency = cart.currency || 'USD';
        for(var i=0; i<bars.length; i++){
          var bar = bars[i];
          var label = bar.querySelector('[data-label]');
          var sub = bar.querySelector('[data-sub]');
          var fill = bar.querySelector('[data-fill]');
          if(remaining <= 0){
            bar.classList.add('is-complete');
            if(label) label.textContent = 'Compliments of the house';
            if(sub) sub.textContent = 'Shipping is on us';
            if(fill) fill.style.width = '100%';
          } else {
            bar.classList.remove('is-complete');
            if(label) label.textContent = 'Compliments of the house';
            if(sub) sub.textContent = formatMoney(remaining, currency) + ' to complimentary shipping';
            if(fill) fill.style.width = pct + '%';
          }
        }
      }).catch(function(){});
  }

  /* ---------- "May we recommend" upsell — product pages only ---------- */
  function buildPDPRecommend(){
    if(document.querySelector('.hh-recommend')) return;
    var path = location.pathname || '';
    if(!/\/products\/[^\/]+/.test(path)) return;
    var handleMatch = path.match(/\/products\/([^\/\?#]+)/);
    if(!handleMatch) return;
    var handle = handleMatch[1];
    /* Find a mount point: end of the main product info, before related products / footer */
    var mount = document.querySelector('main') || document.querySelector('#MainContent') || document.body;
    if(!mount) return;
    /* Fetch the current product first to get its id, then ask for related items.
       Falls back to /products.json when /recommendations returns nothing,
       and falls back to sold-out items when no in-stock alternatives exist. */
    fetch('/products/' + handle + '.js', {credentials:'same-origin', headers:{'Accept':'application/json'}})
      .then(function(r){ return r.json(); })
      .then(function(currentProduct){
        var currentId = currentProduct && currentProduct.id;
        function pickFrom(products){
          var inStock = null;
          var anyOther = null;
          for(var i = 0; i < products.length; i++){
            var p = products[i];
            if(!p || p.id === currentId) continue;
            if(!anyOther) anyOther = p;
            if(p.available !== false && !inStock) inStock = p;
          }
          return inStock || anyOther || null;
        }
        var primary = currentId
          ? '/recommendations/products.json?product_id=' + currentId + '&intent=related&limit=6'
          : '/products.json?limit=6';
        return fetch(primary, {credentials:'same-origin', headers:{'Accept':'application/json'}})
          .then(function(r){ return r.json(); })
          .then(function(data){
            var pick = pickFrom(data.products || []);
            if(pick) return pick;
            /* Fallback: browse the storefront catalog directly */
            return fetch('/products.json?limit=12', {credentials:'same-origin', headers:{'Accept':'application/json'}})
              .then(function(r){ return r.json(); })
              .then(function(d2){ return pickFrom(d2.products || []); });
          });
      })
      .then(function(product){
        if(!product || document.querySelector('.hh-recommend')) return;
        var img = (product.images && product.images[0]) || (product.featured_image) || '';
        var imgUrl = typeof img === 'string' ? img : (img && img.src) || '';
        var firstVariant = (product.variants && product.variants[0]) || {};
        var variantId = firstVariant.id || product.variant_id;
        var price = firstVariant.price || product.price || 0;
        if(typeof price === 'number' && price > 1000) price = (price / 100).toFixed(2);
        var url = product.url || ('/products/' + (product.handle || ''));
        var card = document.createElement('div');
        card.className = 'hh-recommend hh-recommend--pdp';
        card.innerHTML = ''
          + '<div class="hh-recommend__label">May we recommend</div>'
          + '<div class="hh-recommend__rule"></div>'
          + '<div class="hh-recommend__item">'
          +   '<a href="' + url + '" class="hh-recommend__img" style="background-image:url(' + imgUrl + ');" aria-label="' + (product.title || '') + '"></a>'
          +   '<div class="hh-recommend__meta">'
          +     '<a href="' + url + '" class="hh-recommend__title" style="color:inherit;text-decoration:none">' + (product.title || 'Suggested') + '</a>'
          +     '<div class="hh-recommend__price">$' + price + '</div>'
          +   '</div>'
          +   '<button type="button" class="hh-recommend__add" data-variant="' + variantId + '">Add to folio</button>'
          + '</div>';
        /* Insert near the end of main, before any existing related-products section */
        var existingRelated = mount.querySelector('product-recommendations-component, [class*="recommendations" i], [class*="related-products" i]');
        if(existingRelated && existingRelated.parentNode){
          existingRelated.parentNode.insertBefore(card, existingRelated);
        } else {
          mount.appendChild(card);
        }
        var btn = card.querySelector('.hh-recommend__add');
        if(btn){
          btn.addEventListener('click', function(){
            btn.setAttribute('disabled','');
            btn.textContent = 'Adding';
            var fd = new FormData();
            fd.append('id', variantId);
            fd.append('quantity', '1');
            fetch('/cart/add.js', {method:'POST', body: fd, credentials:'same-origin', headers:{'Accept':'application/json'}})
              .then(function(){ btn.textContent = 'Added to folio'; refreshShipping(); })
              .catch(function(){ btn.removeAttribute('disabled'); btn.textContent = 'Add to folio'; });
          });
        }
      })
      .catch(function(){});
  }

  /* Keep shipping bar in sync on cart changes */
  function watchShipping(){
    var refresh = function(){ setTimeout(refreshShipping, 250); };
    document.addEventListener('cart:update', refresh);
    document.addEventListener('cart:item-added', refresh);
    document.addEventListener('cart:items-added', refresh);
    document.addEventListener('on:cart:added', refresh);
    var origFetch = window.fetch;
    if(origFetch && !origFetch.__hhShipWrapped){
      var wrapped = function(input, init){
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = ((init && init.method) || (input && input.method) || 'GET').toString().toUpperCase();
        var p = origFetch.apply(this, arguments);
        if(method === 'POST' && /\/cart\/(add|change|update|clear)/.test(url)){
          p.then(function(){ refresh(); }).catch(function(){});
        }
        return p;
      };
      wrapped.__hhShipWrapped = true;
      try { window.fetch = wrapped; } catch(e){}
    }
  }

  /* ---------- Wrong-suite 404 ---------- */
  function build404(){
    if(document.querySelector('.hh-404')) return;
    var is404 = false;
    var bodyTemplate = (document.body && document.body.dataset && (document.body.dataset.template || document.body.dataset.templateName)) || '';
    if(/404|not[-_ ]?found/i.test(bodyTemplate)) is404 = true;
    var title = (document.title || '').toLowerCase();
    if(!is404 && (/404|page not found|not found/.test(title))) is404 = true;
    if(!is404){
      var heading = document.querySelector('h1');
      if(heading && /^(404|page not found)$/i.test(heading.textContent.trim())) is404 = true;
    }
    if(!is404 && document.querySelector('[data-template-name="404"], main[class*="404"], section[class*="not-found"]')) is404 = true;
    if(!is404) return;
    var overlay = document.createElement('div');
    overlay.className = 'hh-404';
    overlay.innerHTML = ''
      + '<div class="hh-404__plaque">'
      +   '<div class="hh-404__brand">Hotel Home</div>'
      +   '<h1 class="hh-404__title">Wrong suite</h1>'
      +   '<div class="hh-404__rule"></div>'
      +   '<div class="hh-404__sub">404 &middot; This room is not available</div>'
      +   '<a class="hh-404__btn" href="/">Return to lobby</a>'
      + '</div>';
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
  }

  /* ---------- Folio receipt on order pages ---------- */
  function buildReceipt(){
    if(document.body.classList.contains('hh-receipt')) return;
    var path = location.pathname || '';
    var isReceipt = /\/thank[-_]?you|\/orders\/|\/account\/orders\/|\/checkouts\/.+\/(thank-you|thank_you)/.test(path);
    if(!isReceipt){
      var template = (document.body && document.body.dataset && (document.body.dataset.template || document.body.dataset.templateName)) || '';
      if(/order|thank/i.test(template)) isReceipt = true;
    }
    if(!isReceipt) return;
    document.body.classList.add('hh-receipt');
    var mark = document.createElement('div');
    mark.className = 'hh-receipt-mark';
    mark.innerHTML = ''
      + '<div class="hh-receipt-mark__brand">Hotel Home</div>'
      + '<div class="hh-receipt-mark__title">Your folio</div>'
      + '<div class="hh-receipt-mark__sub">Suite ' + suiteNumber() + ' &middot; Folio ' + folioNumber() + '</div>'
      + '<div class="hh-receipt-mark__rule"></div>'
      + '<div class="hh-receipt-stamp">Thank you for staying with us</div>';
    var main = document.querySelector('main') || document.body;
    if(main.firstChild){ main.insertBefore(mark, main.firstChild); } else { main.appendChild(mark); }
  }

  var REPLACEMENTS = [
    [/^\s*Subtotal\s*$/i, 'Folio total'],
    [/^\s*Cart\s*$/i, 'Your folio'],
    [/^\s*Your cart\s*$/i, 'Your folio'],
    [/^\s*Your cart is empty\s*$/i, 'Your folio is empty'],
    [/^\s*Check\s?out\s*$/i, 'Settle folio'],
    [/^\s*Continue shopping\s*$/i, 'Return to lobby']
  ];

  function relabelFolio(root){
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var n;
    while((n = walker.nextNode())){
      var t = n.nodeValue;
      if(!t || t.length > 50) continue;
      for(var i = 0; i < REPLACEMENTS.length; i++){
        var re = REPLACEMENTS[i][0];
        if(re.test(t)){
          n.nodeValue = t.replace(re, REPLACEMENTS[i][1]);
          break;
        }
      }
    }
  }

  function watchCartDrawer(){
    function findRoot(){
      return document.querySelector('cart-drawer-component, [is="cart-drawer-component"], .cart-drawer, dialog.drawer[id*="cart"], dialog[aria-label*="Cart" i]');
    }
    var root = findRoot();
    if(root) enhanceFolio(root);
    var obs = new MutationObserver(function(){
      var r = findRoot();
      if(r){
        enhanceFolio(r);
        relabelFolio(r);
      }
    });
    obs.observe(document.body, {childList:true, subtree:true});
  }

  /* ---------- DND newsletter card ---------- */
  function buildDND(){
    var existing = document.querySelector('.hh-dnd');
    if(existing) return;
    var target = document.querySelector('[data-hh-dnd], .hh-dnd-mount');
    if(!target){
      var path = location.pathname;
      var isHomeOrPassword = path === '/' || path === '' || path === '/password';
      if(!isHomeOrPassword) return;
      var form = document.querySelector('form[action*="newsletter"], form[id*="newsletter" i], form[id*="email_signup" i], form#email_signup');
      if(!form){
        var emailForms = Array.prototype.slice.call(document.querySelectorAll('form'));
        for(var k = 0; k < emailForms.length; k++){
          var ef = emailForms[k];
          if(ef.querySelector('input[type="email"]') && !ef.querySelector('textarea')){
            form = ef;
            break;
          }
        }
      }
      if(form && form.parentNode){
        target = document.createElement('div');
        form.parentNode.insertBefore(target, form);
        form.style.display = 'none';
      } else {
        return;
      }
    }
    target.classList.add('hh-dnd');
    target.innerHTML = ''
      + '<div class="hh-dnd__card" tabindex="0" role="button" aria-label="Flip card to subscribe">'
      +   '<div class="hh-dnd__face hh-dnd__face--front">'
      +     '<div class="hh-dnd__hole"></div>'
      +     '<div class="hh-dnd__frame">'
      +       '<div class="hh-dnd__title">Do not<br>disturb</div>'
      +       '<div class="hh-dnd__rule"></div>'
      +       '<div class="hh-dnd__sub">Flip for early access</div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="hh-dnd__face hh-dnd__face--back">'
      +     '<div class="hh-dnd__hole"></div>'
      +     '<div class="hh-dnd__frame">'
      +       '<div class="hh-dnd__title" style="font-size:20px">Service,<br>please</div>'
      +       '<div class="hh-dnd__rule"></div>'
      +       '<form class="hh-dnd__form" action="/contact#newsletter" method="post" accept-charset="UTF-8">'
      +         '<input type="hidden" name="form_type" value="customer">'
      +         '<input type="hidden" name="utf8" value="&#10003;">'
      +         '<input type="hidden" name="contact[tags]" value="newsletter">'
      +         '<input type="email" name="contact[email]" placeholder="your@email.com" required>'
      +         '<button type="submit">Send to my suite</button>'
      +       '</form>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    var card = target.querySelector('.hh-dnd__card');
    function flip(e){
      if(e && e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON')) return;
      target.classList.toggle('is-flipped');
    }
    card.addEventListener('click', flip);
    card.addEventListener('keydown', function(e){
      if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); flip(); }
    });
  }

  /* ---------- Now-arriving rotating marquee ---------- */
  var HH_MARQUEE_LINES = [
    'Free shipping for orders over $200',
    'Concierge available 24/7 — contact us',
    'Today’s amenity: Hotel Home robes',
    'Press the bell. We’ll send for room service'
  ];
  function buildMarquee(){
    /* find the existing announcement bar by text content */
    var candidates = document.querySelectorAll('[class*="announcement" i] *, [class*="marquee" i] *, header *, [class*="banner-bar" i] *');
    var target = null;
    for(var i = 0; i < candidates.length; i++){
      var t = candidates[i].textContent || '';
      if(/free shipping/i.test(t) && t.length < 80 && candidates[i].children.length === 0){
        target = candidates[i];
        break;
      }
    }
    if(!target) return;
    target.innerHTML = '<span class="hh-marquee-text" data-hh-line>' + HH_MARQUEE_LINES[0] + '</span>';
    var idx = 0;
    var line = target.querySelector('[data-hh-line]');
    setInterval(function(){
      idx = (idx + 1) % HH_MARQUEE_LINES.length;
      if(line){
        line.style.animation = 'none';
        void line.offsetWidth;
        line.textContent = HH_MARQUEE_LINES[idx];
        line.style.animation = '';
      }
    }, 7000);
  }

  /* ---------- Concierge call button ---------- */
  function buildConcierge(){
    if(document.querySelector('.hh-concierge')) return;
    var btn = document.createElement('button');
    btn.className = 'hh-concierge';
    btn.setAttribute('type','button');
    btn.setAttribute('aria-label','Concierge');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="12" cy="20" rx="7" ry=".8" fill="rgba(245,240,230,.25)"/><path d="M5 17 L19 17 L17.5 14.5 L6.5 14.5 Z" fill="#F5F0E6"/><path d="M7 14.5 C7 8, 17 8, 17 14.5 Z" fill="#F5F0E6"/><circle cx="12" cy="6" r="1.2" fill="#F5F0E6"/><line x1="12" y1="3.5" x2="12" y2="5.4" stroke="#F5F0E6" stroke-width=".8"/></svg>';
    document.body.appendChild(btn);
    var card = document.createElement('div');
    card.className = 'hh-concierge__card';
    card.innerHTML = ''
      + '<div class="hh-concierge__title">May we assist?</div>'
      + '<div class="hh-concierge__rule"></div>'
      + '<div class="hh-concierge__links">'
      +   '<a href="/account">Track my folio</a>'
      +   '<a href="/pages/care-guide">Care guide</a>'
      +   '<a href="/pages/contact">Contact front desk</a>'
      + '</div>';
    document.body.appendChild(card);
    btn.addEventListener('click', function(e){
      e.stopPropagation();
      card.classList.toggle('is-open');
    });
    document.addEventListener('click', function(e){
      if(!card.contains(e.target) && !btn.contains(e.target)){
        card.classList.remove('is-open');
      }
    });
  }

  /* ---------- First-visit welcome key tag ---------- */
  function buildWelcomeTag(){
    if(localStorage.getItem('hh-welcomed')) return;
    if(prefersReducedMotion){ localStorage.setItem('hh-welcomed','1'); return; }
    var overlay = document.createElement('div');
    overlay.className = 'hh-welcome';
    overlay.innerHTML = ''
      + '<div class="hh-welcome__tag" role="dialog" aria-label="Welcome">'
      +   '<div class="hh-welcome__brand">Hotel Home</div>'
      +   '<div class="hh-welcome__suite">' + suiteNumber() + '</div>'
      +   '<div class="hh-welcome__sub">Welcome, guest</div>'
      +   '<button type="button" class="hh-welcome__close">Check in</button>'
      + '</div>';
    document.body.appendChild(overlay);
    localStorage.setItem('hh-welcomed','1');
    setTimeout(function(){ overlay.classList.add('is-on'); }, 400);
    var dismiss = function(){
      overlay.classList.remove('is-on');
      setTimeout(function(){ if(overlay.parentNode) overlay.parentNode.removeChild(overlay); }, 450);
    };
    overlay.querySelector('.hh-welcome__close').addEventListener('click', dismiss);
    overlay.addEventListener('click', function(e){ if(e.target === overlay) dismiss(); });
    /* auto-dismiss after 5s if the guest hasn't clicked */
    setTimeout(function(){ if(overlay.parentNode) dismiss(); }, 5500);
  }

  /* ---------- Hotel-coded copy relabels ---------- */
  var GLOBAL_REPLACEMENTS = [
    [/\bsold\s*out\b/gi, 'Fully booked'],
    [/\bout\s*of\s*stock\b/gi, 'Fully booked'],
    [/\bWishlist\b/g, 'Saved to my stay'],
    [/\bAdd to wishlist\b/gi, 'Save to my stay'],
    [/\bSave for later\b/gi, 'Save to my stay']
  ];
  function relabelStore(root){
    root = root || document.body;
    if(!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function(node){
        if(!node.parentNode) return NodeFilter.FILTER_REJECT;
        var tag = node.parentNode.tagName;
        if(tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var n;
    while((n = walker.nextNode())){
      var t = n.nodeValue;
      if(!t || t.length > 80) continue;
      var newT = t;
      for(var i = 0; i < GLOBAL_REPLACEMENTS.length; i++){
        newT = newT.replace(GLOBAL_REPLACEMENTS[i][0], GLOBAL_REPLACEMENTS[i][1]);
      }
      if(newT !== t) n.nodeValue = newT;
    }
  }
  function watchRelabels(){
    relabelStore();
    var t;
    var obs = new MutationObserver(function(){
      clearTimeout(t);
      t = setTimeout(function(){ relabelStore(); }, 200);
    });
    obs.observe(document.body, {childList:true, subtree:true});
  }

  /* ---------- boot ---------- */
  function boot(){
    if(document.documentElement.dataset.hhBooted) return;
    document.documentElement.dataset.hhBooted = '1';
    injectStyles();
    buildDoors();
    buildElevator();
    buildBell();
    watchAddToCart();
    watchCartDrawer();
    watchShipping();
    build404();
    buildReceipt();
    buildMarquee();
    buildConcierge();
    buildWelcomeTag();
    watchRelabels();
    setTimeout(buildPDPRecommend, 400);
    setTimeout(buildDND, 300);
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
